import { TranslatorSidebar } from "@/components/TranslatorSidebar";
import { useMicrophoneState, MicrophoneProvider } from "@/contexts/MicrophoneContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ResponsiveProvider } from "@/contexts/ResponsiveContext";
import { useWebRTCSession, WebRTCSessionProvider } from "@/contexts/WebRTCSessionContext";
import { useAuth, AuthProvider } from "@/contexts/AuthContext";
import { useFreeTrialContext, FreeTrialProvider } from "@/contexts/FreeTrialContext";
import { AuthFreeTrialBridge } from "@/components/shared/AuthFreeTrialBridge";
import { I18nProvider, useTranslation } from "@/contexts/I18nContext";
import { Auth0ProviderWrapper } from "@/lib/auth";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { ChatsPage } from "@/components/pages/ChatsPage";
import { SettingsPage } from "@/components/pages/SettingsPage";
import { LoginPage } from "@/components/pages/LoginPage";
import { AuthCallbackPage } from "@/components/pages/AuthCallbackPage";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";

import { Toaster } from "@/components/ui/sonner";
import { AudioRecorderWithVisualizer } from "@/components/Audio/audio-recorder-with-visualizer";
import { Timer } from "@/components/ui/Timer";
import { LanguagePairSelector } from "@/components/shared/LanguagePairSelector";
import { TokenStatusIndicator } from "@/components/shared/TokenStatusIndicator";
import { useState, useEffect } from 'react';
import { SessionConnectionStatus } from "@/services/interfaces/Session";
import { CollapsibleRealtimeConsole } from "@/components/shared/CollapsibleRealtimeConsole";

import { router, AppRoute } from "@/lib/router";
import { PageTransition } from "@/components/pages/PageTransition";
import { PurchasePage } from "@/components/pages/PurchasePage";
import { OrderHistoryPage } from '@/components/pages/OrderHistoryPage';
import { FreeTrialBanner } from "@/components/shared/FreeTrialBanner";
import { AuthRequiredMessage } from "@/components/shared/AuthRequiredMessage";
import { AdminPage } from "@/components/admin/AdminPage";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { useI18n } from "@/contexts/I18nContext";


function EntryContent() {
    const { t } = useTranslation();
    const { currentLanguage } = useI18n();

    // 路由状态管理
    const [currentRoute, setCurrentRoute] = useState<AppRoute>(router.getCurrentRoute());

    // 认证状态
    const { isAuthenticated, isLoading: authLoading } = useAuth();

    // 免费试用状态
    const { isInFreeTrial, isLoading: freeTrialLoading } = useFreeTrialContext();
    
    // 监听路由变化
    useEffect(() => {
        const unsubscribe = router.subscribe((route) => {
            setCurrentRoute(route);
        });
        return unsubscribe;
    }, []);

    // 同步路由器语言设置
    useEffect(() => {
        router.setLanguage(currentLanguage);
    }, [currentLanguage]);

    // 修改认证保护逻辑：允许免费试用用户访问主页，管理员页面有独立认证
    useEffect(() => {
        if (!authLoading && !isAuthenticated && !isInFreeTrial &&
            currentRoute !== AppRoute.LOGIN && currentRoute !== AppRoute.ADMIN) {
            // 如果用户未登录且没有免费试用，只有访问主页时不重定向到登录页
            if (currentRoute !== AppRoute.HOME) {
                router.navigate(AppRoute.LOGIN);
            }
        }
    }, [isAuthenticated, authLoading, isInFreeTrial, currentRoute]);
    
    // 使用 WebRTC session context
    const {
        isSessionActive,
        sessionConnectionStatus,
        stopTranslation
    } = useWebRTCSession();

    // 计时器状态
    const [timer, setTimer] = useState<number>(0);

    // 计时器效果 - 当会话激活时开始计时
    useEffect(() => {
        let timerInterval: NodeJS.Timeout;
        
        if (isSessionActive) {
            timerInterval = setInterval(() => {
                setTimer(prev => prev + 1);
            }, 1000);
        } else {
            // 会话结束时重置计时器
            setTimer(0);
        }

        return () => {
            if (timerInterval) {
                clearInterval(timerInterval);
            }
        };
    }, [isSessionActive]);

    // 停止翻译
    const handleStopTranslation = async () => {
        try {
            await stopTranslation();
        } catch (err) {
            console.error("Failed to stop translation:", err);
        }
    };

    // 确定是否正在加载
    const isLoading = sessionConnectionStatus === SessionConnectionStatus.Connecting;

    // 如果正在加载认证状态或免费试用状态，显示加载界面
    if (authLoading || freeTrialLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    // 如果在登录页面，显示独立的登录界面
    if (currentRoute === AppRoute.LOGIN) {
        return <LoginPage />;
    }

    // 如果在Auth回调页面，显示回调处理界面
    if (currentRoute === AppRoute.AUTH_CALLBACK) {
        return <AuthCallbackPage />;
    }

    // 如果在管理员页面，显示独立的管理员界面
    if (currentRoute === AppRoute.ADMIN) {
        return <AdminPage />;
    }

    // 修改认证检查：允许免费试用用户访问主页
    if (!isAuthenticated && !isInFreeTrial && currentRoute !== AppRoute.HOME) {
        return null;
    }

    // 渲染对应的页面内容
    const renderPageContent = () => {
        switch (currentRoute) {
            case AppRoute.CHATS:
                // 需要登录才能访问聊天记录
                if (!isAuthenticated) {
                    return <AuthRequiredMessage message="需要登录才能查看聊天记录" />;
                }
                return <ChatsPage />;
            case AppRoute.SETTINGS:
                // 需要登录才能访问设置
                if (!isAuthenticated) {
                    return <AuthRequiredMessage message="需要登录才能访问设置" />;
                }
                return <SettingsPage />;
            case AppRoute.PURCHASE:
                // 需要登录才能购买
                if (!isAuthenticated) {
                    return <AuthRequiredMessage message="需要登录才能购买" />;
                }
                return <PurchasePage />;
            case AppRoute.ORDER_HISTORY:
                // 需要登录才能查看订单历史
                if (!isAuthenticated) {
                    return <AuthRequiredMessage message="需要登录才能查看订单历史" />;
                }
                return <OrderHistoryPage />;
            case AppRoute.HOME:
            default:
                return (
                    <PageTransition className="h-full" />
                );
        }
    };

    return (
        <SidebarProvider>
            {/* 🔑 桥接组件：连接AuthContext和FreeTrialContext */}
            <AuthFreeTrialBridge />

            <TranslatorSidebar
                currentRoute={currentRoute}
            />
            <SidebarInset>
                {/* 🔑 固定状态栏 - 翻译页面在移动端隐藏 */}
                <header className={`sticky top-0 z-50 flex h-16 shrink-0 items-center gap-2 border-b bg-background ${
                    currentRoute === AppRoute.HOME && isSessionActive ? 'hidden sm:flex' : 'flex'
                }`}>
                    <div className="flex items-center gap-2 px-4 flex-1">
                        <div className="flex items-center gap-2">
                            <SidebarTrigger className="-ml-1" />
                            {/* <Separator orientation="vertical" className="mr-2 h-4" /> */}
                            <Breadcrumb>
                                <BreadcrumbList>
                                    {/* <BreadcrumbItem className="hidden md:block">
                                        <BreadcrumbLink href="#">
                                            双语翻译器
                                        </BreadcrumbLink>
                                    </BreadcrumbItem> */}
                                    {/* <BreadcrumbSeparator className="hidden md:block" /> */}
                                    <BreadcrumbItem>
                                        <BreadcrumbPage>
                                            {router.getRouteTitle(currentRoute)}
                                        </BreadcrumbPage>
                                    </BreadcrumbItem>
                                </BreadcrumbList>
                            </Breadcrumb>
                        </div>

                        {/* Token 状态显示 - 右侧 */}
                        <div className="ml-auto">
                            <TokenStatusIndicator compact />
                        </div>
                    </div>
                </header>
                
                {/* 🔑 免费试用横幅 - 录音页面隐藏 */}
                {!(currentRoute === AppRoute.HOME && isSessionActive) && (
                    <FreeTrialBanner />
                )}
                
                {/* 🔑 语言选择器 - 移动端调整定位 */}
                {currentRoute === AppRoute.HOME && isSessionActive && (
                    <div className={`sticky border-b bg-background z-40 shrink-0 ${
                        'top-0 sm:top-16'
                    }`}>
                        <div
                            id="language-selector-container"
                            className="max-w-xl mx-auto"
                        >
                            <LanguagePairSelector disabled={true} className="h-10" compact={true} />
                        </div>
                    </div>
                )}
                
                <div className="flex-1 overflow-y-auto">
                    {renderPageContent()}
                </div>
                
                 {/* 折叠的实时控制台 - 只在开发模式下显示 */}
                {import.meta.env.DEV && currentRoute === AppRoute.HOME && isSessionActive && (
                    <CollapsibleRealtimeConsole
                        isSessionActive={isSessionActive}
                        sessionConnectionStatus={sessionConnectionStatus}
                    />
                )}
                
                {/* 🔑 底部：音频可视化和控制按钮 - 移动端缩小高度 */}
                {currentRoute === AppRoute.HOME && isSessionActive && (
                    <div className="sticky bottom-0 z-50 border-t bg-background shrink-0">
                        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-4">
                            <div className="max-w-2xl mx-auto space-y-0">
                                {/* 🔑 Timer 组件 - 移动端去掉边框、缩小字体和间距 */}
                                <div className="flex justify-start mb-0.5 sm:mb-2">
                                    <Timer
                                        timer={timer}
                                        isVisible={isSessionActive}
                                        sessionConnectionStatus={sessionConnectionStatus}
                                    />
                                </div>

                                {/* 音频可视化 - 移动端缩小高度 */}
                                <AudioRecorderWithVisualizer
                                    className="w-full max-w-full h-10 sm:h-16"
                                    isRecording={isSessionActive}
                                    sessionConnectionStatus={sessionConnectionStatus}
                                    onPause={handleStopTranslation}
                                    isLoading={isLoading}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {/* <AppFooter /> */}
            </SidebarInset>
            
            {/* 🧪 开发环境测试组件 */}
            {/* <TokenTestComponent /> */}
        </SidebarProvider>
    );
}

function Entry() {
    return (
        <I18nProvider>
            <ResponsiveProvider>
                <Auth0ProviderWrapper>
                    <AuthProvider>
                         <FreeTrialProvider>
                            <AdminAuthProvider>
                                <MicrophoneProvider>
                                     <LanguageProvider>
                                        <WebRTCWrapper>
                                            <EntryContent />
                                             <Toaster />
                                        </WebRTCWrapper>
                                     </LanguageProvider>
                                 </MicrophoneProvider>
                            </AdminAuthProvider>
                         </FreeTrialProvider>
                    </AuthProvider>
                </Auth0ProviderWrapper>
             </ResponsiveProvider>
        </I18nProvider>
    );
}

// 🔑 新增：WebRTC 包装器组件，用于获取麦克风服务并传递给 WebRTCSessionProvider
function WebRTCWrapper({ children }: { children: React.ReactNode }) {
    const { microphoneService } = useMicrophoneState();
    
    return (
        <WebRTCSessionProvider 
            backendUrl={import.meta.env.VITE_BACKEND_URL}
            microphoneService={microphoneService}
        >
            {children}
        </WebRTCSessionProvider>
    );
}

export default Entry;